// frontend/src/components/SmartDictation/SmartDictation.js

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TemplateContext } from '../../context/TemplateContext';
import { UserContext } from '../../UserContext';
import TopBar from '../TopBar/TopBar';
import JournalTab from '../JournalEditor/components/JournalTab';
import DocumentsTab from '../JournalEditor/components/DocumentsTab';
import DocumentTab from '../DocumentTab/DocumentTab';
import apiClient from '../../apiClient';
import { toast } from 'react-toastify';
import './SmartDictation.css';

// Import JournalEditor hooks and utilities
import { useJournalSections } from '../JournalEditor/hooks/useJournalSections';
import { useDocumentGeneration } from '../JournalEditor/hooks/useDocumentGeneration';
import { useJournalTransfer } from '../JournalEditor/hooks/useJournalTransfer';
import { useSession } from '../../hooks/useSession';
import { useMicrophones } from '../../hooks/useMicrophones';
import { markRecordingActive } from '../../services/recordingActivity';
import {
  applyOrderFromTemplate,
  extractSectionContent
} from '../JournalEditor/utils/sectionHelpers';

const CHUNK_INTERVAL = 2; // Seconds between chunks during streaming
const CHUNK_START_AFTER = 3; // Start chunking after X seconds

function SmartDictation() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { templateId, setTemplateId } = useContext(TemplateContext);
  
  // URL parameters for session restoration
  const [searchParams] = useSearchParams();
  const sessionIdFromURL = searchParams.get('session_id') || null;
  
  // Template management
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateLocked, setTemplateLocked] = useState(false);
  
  // Session management with useSession hook
  const { sessionId, setSessionId, createSession: createSessionHook, fetchSessionData } = useSession(sessionIdFromURL, false, 'session');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Microphone management
  const { microphones, selectedMicId, setSelectedMicId } = useMicrophones();
  
  // Dictation state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [streamingText, setStreamingText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [dictationStarted, setDictationStarted] = useState(false);

  // Signalera pågående diktering (även pausad), se services/recordingActivity.
  const dictationActive = isRecording || isPaused;
  useEffect(() => (dictationActive ? markRecordingActive() : undefined), [dictationActive]);

  // Tab management
  const [currentTab, setCurrentTab] = useState('journal');
  
  // Audio handling
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const startTimeRef = useRef(null);
  const totalElapsedRef = useRef(0);
  const timerRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const lastChunkSecondRef = useRef(-1);
  const isRecordingRef = useRef(false); // Use ref to avoid stale closures
  const chunkIndexRef = useRef(0); // Track chunk index for prioritization
  
  // Transcription data
  const accumulatedTextRef = useRef('');
  const cleanedTextRef = useRef('');
  const lastActivityRef = useRef(Date.now());
  const cleanupTimeoutRef = useRef(null);
  
  const CLEANUP_DELAY = 2000; // 2 seconds of silence before cleanup

  // Journal sections state (managed locally for dictation)
  const [journalSections, setJournalSections] = useState([]);
  const [editingSectionId, setEditingSectionId] = useState(null);
  
  // Clear template context on mount (unless restoring session)
  useEffect(() => {
    if (!sessionIdFromURL) {
      setTemplateId(null);
    }
  }, []);
  
  // Section handling functions
  const handleSectionChange = async (sectionIndex, newContent) => {
    const updatedSections = [...journalSections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      content: newContent
    };
    setJournalSections(updatedSections);
    setHasUnsavedChanges(true);
  };

  const handleDeleteSection = async (sectionIndex) => {
    const updatedSections = journalSections.filter((_, index) => index !== sectionIndex);
    setJournalSections(updatedSections);
    setHasUnsavedChanges(true);
  };

  const handleCopySection = (sectionIndex) => {
    const section = journalSections[sectionIndex];
    if (section && section.content) {
      navigator.clipboard.writeText(section.content);
      toast.success('Innehåll kopierat!');
    }
  };

  const handleDictateSection = (sectionIndex) => {
    // This would be implementation for dictating directly into a section
    // For now, we'll just show a message
    toast.info('Diktering direkt i sektioner kommer i framtida uppdatering');
  };

  // Document generation hook
  const {
    openDocs: activeDocuments = [],
    generateDocument = () => {},
    closeDocument = () => {},
    availableTemplates: availableDocumentTemplates = [],
    generatingDocs: generatingDocuments = []
  } = useDocumentGeneration(sessionId) || {};

  // Wrapper function for document generation
  const handleGenerateDocument = async (template) => {
    const journalText = journalSections
      .map(section => `${section.title}\n${section.content.trim()}`)
      .join('\n\n');
    
    // The generateDocument function will automatically switch to the new tab
    // by using the setCurrentTab callback we pass as third parameter
    await generateDocument(template, journalText, (docId) => {
      // docId comes as 'doc-tmpl-X', we need 'document-tmpl-X'
      const tabId = docId.replace('doc-', 'document-');
      setCurrentTab(tabId);
    });
  };

  // Transfer functionality hook
  const {
    handleTransferClick: handleJournalTransfer
  } = useJournalTransfer(journalSections, selectedTemplate, sessionId);
  
  // Local transfer state
  const [transferStatus, setTransferStatus] = useState('idle');
  const canTransfer = journalSections.some(section => section.content.trim().length > 0);
  
  const handleTransfer = async () => {
    if (!canTransfer) return;
    
    setTransferStatus('transferring');
    try {
      await handleJournalTransfer();
      setTransferStatus('completed');
      toast.success('Journal överförd till journalsystem!');
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error('Kunde inte överföra journal');
      setTransferStatus('error');
    } finally {
      setTimeout(() => setTransferStatus('idle'), 2000);
    }
  };
  
  // Handle document closing
  const handleCloseDocument = (docId) => {
    closeDocument(docId);
    // Switch back to journal tab if current tab was the closed document
    if (currentTab === `document-${docId}`) {
      setCurrentTab('journal');
    }
  };

  // ===========================================================
  // Template Management
  // ===========================================================
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await apiClient.get('/api/journalmallar');
        if (Array.isArray(resp.data)) {
          setTemplates(resp.data);
          
          // If templateId already exists, select it
          if (templateId) {
            const template = resp.data.find(t => t.template_id === parseInt(templateId));
            if (template) {
              setSelectedTemplate(template);
              // Initialize journal sections only if not restoring from session
              if (template.sections && !sessionIdFromURL) {
                const sections = template.sections
                  .sort((a, b) => a.order - b.order)
                  .map(section => ({
                    section_id: null,
                    title: section.section_title,
                    content: '',
                    order: section.order,
                    isEditing: false
                  }));
                setJournalSections(sections);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error('Kunde inte hämta journalmallar');
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [templateId, sessionIdFromURL]);

  // ===========================================================
  // Session Restoration
  // ===========================================================
  useEffect(() => {
    if (sessionIdFromURL && templates.length > 0) {
      handleSessionRestore(sessionIdFromURL);
    }
  }, [sessionIdFromURL, templates]);

  const handleSessionRestore = async (sid) => {
    console.log('Restoring SmartDictation session:', sid);
    try {
      const sessionData = await fetchSessionData(sid);
      const {
        status,
        transcription_text,
        generated_journal,
        template_id,
      } = sessionData;
      
      console.log('Session data fetched for SmartDictation:', {
        status, 
        transcription_text: transcription_text?.length,
        template_id,
        generated_journal: generated_journal?.length
      });

      // Set template if specified
      if (template_id) {
        setTemplateId(template_id);
        const template = templates.find(t => t.template_id === template_id);
        if (template) {
          setSelectedTemplate(template);
          setTemplateLocked(true); // Lock template since session exists
          setDictationStarted(true); // Mark as started
        }
      }

      // Restore transcription text
      if (transcription_text) {
        accumulatedTextRef.current = transcription_text;
        setStreamingText(transcription_text);
      }

      // Restore journal sections from generated_journal
      if (generated_journal && selectedTemplate?.sections) {
        // Parse the generated journal back into sections
        const restoredSections = parseJournalBackToSections(generated_journal, selectedTemplate.sections);
        setJournalSections(restoredSections);
      } else if (selectedTemplate?.sections) {
        // Initialize empty sections if no journal data yet
        const sections = selectedTemplate.sections
          .sort((a, b) => a.order - b.order)
          .map(section => ({
            section_id: null,
            title: section.section_title,
            content: '',
            order: section.order,
            isEditing: false
          }));
        setJournalSections(sections);
      }
      
    } catch (err) {
      console.error('Error restoring SmartDictation session:', err);
      toast.error('Kunde inte återställa session');
    }
  };

  const parseJournalBackToSections = (journalText, templateSections) => {
    const sections = templateSections
      .sort((a, b) => a.order - b.order)
      .map(section => ({
        section_id: null,
        title: section.section_title,
        content: '',
        order: section.order,
        isEditing: false
      }));

    if (!journalText) return sections;

    // Simple parsing - split by section titles
    const lines = journalText.split('\n');
    let currentSectionIndex = -1;
    let currentContent = [];

    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Check if this line matches a section title
      const sectionIndex = sections.findIndex(s => s.title === trimmedLine);
      
      if (sectionIndex !== -1) {
        // Save previous section content
        if (currentSectionIndex !== -1 && currentContent.length > 0) {
          sections[currentSectionIndex].content = currentContent.join('\n').trim();
        }
        
        // Start new section
        currentSectionIndex = sectionIndex;
        currentContent = [];
      } else if (currentSectionIndex !== -1 && trimmedLine) {
        // Add to current section content
        currentContent.push(line);
      }
    }

    // Don't forget the last section
    if (currentSectionIndex !== -1 && currentContent.length > 0) {
      sections[currentSectionIndex].content = currentContent.join('\n').trim();
    }

    return sections;
  };

  const handleTemplateChange = (newTemplateId) => {
    if (templateLocked) {
      toast.error('Du kan inte ändra mall efter att diktering har börjat');
      return;
    }
    
    const template = templates.find(t => t.template_id === parseInt(newTemplateId));
    if (template) {
      setSelectedTemplate(template);
      setTemplateId(newTemplateId);
      
      // Initialize journal sections from template
      if (template.sections) {
        const sections = template.sections
          .sort((a, b) => a.order - b.order)
          .map(section => ({
            section_id: null,
            title: section.section_title,
            content: '',
            order: section.order,
            isEditing: false
          }));
        setJournalSections(sections);
      }
      
      // Reset dictation state when changing template
      setStreamingText('');
      accumulatedTextRef.current = '';
      cleanedTextRef.current = '';
      
      // Clear any cleanup timeout
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    }
  };

  // ===========================================================
  // Session Management
  // ===========================================================
  const createSession = async () => {
    if (!selectedTemplate) return null;
    
    try {
      // Create session directly with diktering status
      const sessionData = {
        template_id: selectedTemplate.template_id,
        status: 'diktering'
      };
      
      const response = await apiClient.post('/recorder/sessions', sessionData);
      const newSessionId = response.data.session_id || response.data.id;
      setSessionId(newSessionId);
      
      // Journal sections should already be initialized from template selection
      // Only initialize if they don't exist yet
      if (journalSections.length === 0 && selectedTemplate.sections) {
        const sections = selectedTemplate.sections
          .sort((a, b) => a.order - b.order)
          .map(section => ({
            section_id: null,
            title: section.section_title,
            content: '',
            order: section.order,
            isEditing: false
          }));
        setJournalSections(sections);
      }
      
      return newSessionId;
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Kunde inte skapa session');
      return null;
    }
  };

  const saveSession = async () => {
    if (!sessionId || !hasUnsavedChanges) return;
    
    try {
      const journalText = journalSections
        .map(section => `${section.title}\n${section.content.trim()}`)
        .join('\n\n');
        
      // Update journal using the correct PATCH endpoint
      if (journalText.trim()) {
        await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
          generated_journal: journalText
        });
        console.log('Journal data saved to session');
      }
      
      // Update transcription using the correct PATCH endpoint  
      if (accumulatedTextRef.current.trim()) {
        await apiClient.patch(`/recorder/sessions/${sessionId}/update_transcription`, {
          transcription_text: accumulatedTextRef.current
        });
        console.log('Transcription data saved to session');
      }
      
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving session:', error);
      // Don't show error toast for auto-save to avoid annoying user
    }
  };

  // Auto-save when changes occur
  useEffect(() => {
    if (hasUnsavedChanges) {
      const timer = setTimeout(() => {
        saveSession();
      }, 1000); // Save after 1 second of inactivity
      
      return () => clearTimeout(timer);
    }
  }, [hasUnsavedChanges, journalSections, streamingText]);

  // Auto-save before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasUnsavedChanges && sessionId) {
        // Trigger save on page unload
        saveSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, sessionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Save before cleanup
      if (hasUnsavedChanges && sessionId) {
        saveSession();
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (chunkTimerRef.current) {
        clearInterval(chunkTimerRef.current);
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // ===========================================================
  // Audio Recording Functions
  // ===========================================================
  const startRecording = async () => {
    if (!selectedTemplate) {
      toast.error('Välj en journalmall först');
      return;
    }

    // Lock template selection
    setTemplateLocked(true);
    setDictationStarted(true);
    
    
    // Create session if it doesn't exist
    if (!sessionId) {
      const newSessionId = await createSession();
      if (!newSessionId) return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          deviceId: selectedMicId ? { exact: selectedMicId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      audioStreamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      let audioChunks = [];
      
      // Store chunks for manual processing
      mediaRecorder.chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          mediaRecorder.chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Final processing if needed
        audioChunks = [];
      };
      
      startTimeRef.current = Date.now();
      totalElapsedRef.current = 0;
      setIsRecording(true);
      setIsPaused(false);
      isRecordingRef.current = true;
      chunkIndexRef.current = 0; // Reset chunk index when starting
      
      // Start recording without timeslice (manual chunking)
      mediaRecorder.start();
      startTimer();
      startChunkTimer();
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Kunde inte starta inspelning. Kontrollera mikrofon-tillstånd.');
    }
  };

  const pauseRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsRecording(false);
      setIsPaused(true);
      isRecordingRef.current = false;
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (chunkTimerRef.current) {
        clearInterval(chunkTimerRef.current);
      }
      
      // Clear any pending cleanup timeout
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      
      // Perform cleanup on pause
      if (accumulatedTextRef.current.trim()) {
        await performTextCleanup();
      }
      
      // Update total elapsed time
      if (startTimeRef.current) {
        totalElapsedRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume();
      setIsRecording(true);
      setIsPaused(false);
      isRecordingRef.current = true;
      
      startTimeRef.current = Date.now();
      startTimer();
      startChunkTimer();
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current) {
      // Send final chunk with high priority flag
      const finalBlob = await exportChunk();
      if (finalBlob) {
        await sendAudioForTranscription(finalBlob, true); // Mark as final chunk
      }
      
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      isRecordingRef.current = false;
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      if (chunkTimerRef.current) {
        clearInterval(chunkTimerRef.current);
      }
      
      // Clear any pending cleanup timeout
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      
      // Perform cleanup on stop
      if (accumulatedTextRef.current.trim()) {
        await performTextCleanup();
      }
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Update total elapsed time
      if (startTimeRef.current) {
        totalElapsedRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
      
      // Reset chunk index when stopping
      chunkIndexRef.current = 0;
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current + totalElapsedRef.current) / 1000);
        setCurrentTime(elapsed);
      }
    }, 1000);
  };

  const startChunkTimer = () => {
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
    }
    
    chunkTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const totalMs = totalElapsedRef.current + elapsed;
      const totalSec = Math.floor(totalMs / 1000);

      if (
        totalSec >= CHUNK_START_AFTER &&
        (totalSec - CHUNK_START_AFTER) % CHUNK_INTERVAL === 0 &&
        totalSec !== lastChunkSecondRef.current
      ) {
        lastChunkSecondRef.current = totalSec;
        exportChunk().then(async (blob) => {
          if (blob) {
            await sendAudioForTranscription(blob);
          }
        });
      }
    }, 1000);
  };

  const exportChunk = async () => {
    if (!mediaRecorderRef.current || !isRecordingRef.current) {
      return null;
    }
    const recorder = mediaRecorderRef.current;
    
    return new Promise((resolve) => {
      recorder.onstop = async () => {
        const blob = new Blob(recorder.chunks || [], { type: recorder.mimeType });
        const chunkCount = recorder.chunks ? recorder.chunks.length : 0;
        recorder.chunks = [];

        if (isRecordingRef.current) {
          try {
            const newRecorder = new MediaRecorder(audioStreamRef.current, {
              mimeType: recorder.mimeType,
            });
            newRecorder.chunks = [];
            newRecorder.ondataavailable = (event) => {
              if (event.data && event.data.size > 0) {
                newRecorder.chunks.push(event.data);
              }
            };
            newRecorder.start();
            mediaRecorderRef.current = newRecorder;
          } catch (error) {
            console.error('Could not restart recording:', error);
          }
        }
        resolve(blob);
      };

      if (!recorder.chunks) {
        recorder.chunks = [];
      }
      
      recorder.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) {
          recorder.chunks.push(evt.data);
        }
      };
      
      recorder.stop();
    });
  };


  const sendAudioForTranscription = async (audioBlob, isFinalChunk = false) => {
    if (!audioBlob) return;
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'chunk.wav');
      
      // Add session metadata for prioritization
      formData.append('session_id', sessionId || `smart_dictation_${Date.now()}`);
      formData.append('chunk_index', chunkIndexRef.current.toString());
      
      // Calculate total duration in seconds
      const totalDuration = Math.floor((totalElapsedRef.current + (startTimeRef.current ? Date.now() - startTimeRef.current : 0)) / 1000);
      formData.append('total_duration', totalDuration.toString());
      formData.append('is_final', isFinalChunk.toString());
      
      // Use new streaming endpoint with session tracking
      const response = await apiClient.post('/api/dictation/streaming_chunk', formData);
      
      // Handle different response types
      if (response.data.status === 'queued') {
        console.log(`Chunk ${chunkIndexRef.current} queued with priority:`, response.data.priority);
      } else if (response.data.status === 'completed' || response.data.transcription) {
        const transcription = response.data.transcription || '';
        const trimmed = transcription.trim();
        
        if (trimmed) {
          accumulatedTextRef.current += (accumulatedTextRef.current ? ' ' : '') + trimmed;
          setStreamingText(accumulatedTextRef.current);
          setHasUnsavedChanges(true);
          
          // Log medical corrections if applied
          if (response.data.medical_correction_applied && response.data.corrections?.length > 0) {
            console.log('Medical corrections applied:', response.data.corrections);
          }
        }
      }
      
      // Increment chunk index for next chunk
      chunkIndexRef.current += 1;
      
    } catch (error) {
      console.error('Error during chunk transcription:', error);
      // On error, still increment chunk index to maintain sequence
      chunkIndexRef.current += 1;
    }
  };

  const performTextCleanup = async () => {
    if (!accumulatedTextRef.current.trim()) return;
    
    try {
      const response = await apiClient.post('/api/dictation/cleanup', {
        raw_text: accumulatedTextRef.current,
        preserve_medical_terms: true,
        language: 'swedish'
      });
      
      const cleaned = response.data?.success ? response.data.cleaned_text : accumulatedTextRef.current;
      
      // Update the streaming text with cleaned version
      cleanedTextRef.current = cleaned;
      setStreamingText(cleaned);
      accumulatedTextRef.current = cleaned;
      
    } catch (error) {
      console.error('Cleanup error:', error);
      // Keep raw text if cleanup fails
    }
  };

  // ===========================================================
  // Create Journal Entry
  // ===========================================================
  const handleCreateJournalEntry = async () => {
    if (!selectedTemplate) {
      toast.error('Ingen journalmall vald');
      return;
    }
    
    setIsProcessing(true);
    
    // If recording is active, pause it first and wait for cleanup
    if (isRecording) {
      await pauseRecording();
      // Wait longer to ensure cleanup completes properly
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    // Clear any pending cleanup timeout and perform final cleanup
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }
    
    // Always perform cleanup before creating journal entry to ensure we have clean text
    if (accumulatedTextRef.current.trim()) {
      await performTextCleanup();
      // Add additional wait after cleanup to ensure it's fully processed
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    if (!streamingText.trim()) {
      toast.error('Ingen text att skapa anteckning från');
      setIsProcessing(false);
      return;
    }
    
    try {
      // Parse the dictated text and split by section headers
      const updatedSections = [...journalSections];
      const text = streamingText.trim();
      
      // Create a map of section titles (case insensitive) to section indices
      const sectionMap = {};
      updatedSections.forEach((section, index) => {
        const normalizedTitle = section.title.toLowerCase().trim();
        sectionMap[normalizedTitle] = index;
        // Also add version with colon for cleanup compatibility
        sectionMap[normalizedTitle + ':'] = index;
      });
      
      // Split text by detecting section headers
      const words = text.split(/\s+/);
      let currentSectionIndex = null;
      let currentContent = [];
      let sectionsToUpdate = [];
      
      for (let i = 0; i < words.length; i++) {
        const word = words[i].toLowerCase();
        
        // Check if this word (or combination of words) matches a section title
        let matchFound = false;
        
        // Try different combinations starting from current word
        for (let j = i; j < Math.min(i + 4, words.length); j++) {
          const phrase = words.slice(i, j + 1).join(' ').toLowerCase();
          // Also try phrase without trailing punctuation (colon, period, etc.)
          const cleanPhrase = phrase.replace(/[:.;,]+$/, '');
          
          if (sectionMap.hasOwnProperty(phrase) || sectionMap.hasOwnProperty(cleanPhrase)) {
            // Found a section header
            
            if (currentSectionIndex !== null && currentContent.length > 0) {
              // Save previous section content
              sectionsToUpdate.push({
                index: currentSectionIndex,
                content: currentContent.join(' ').trim()
              });
            }
            
            currentSectionIndex = sectionMap[phrase] || sectionMap[cleanPhrase];
            currentContent = [];
            i = j; // Skip the words we just processed
            matchFound = true;
            break;
          }
        }
        
        if (!matchFound) {
          // This word is content, add it to current section
          currentContent.push(words[i]);
        }
      }
      
      // Don't forget the last section
      if (currentSectionIndex !== null && currentContent.length > 0) {
        sectionsToUpdate.push({
          index: currentSectionIndex,
          content: currentContent.join(' ').trim()
        });
      }
      
      // If no sections were found, put everything in the first section
      if (sectionsToUpdate.length === 0) {
        sectionsToUpdate.push({
          index: 0,
          content: text
        });
      }
      
      // Apply updates to sections
      sectionsToUpdate.forEach(update => {
        const currentContent = updatedSections[update.index].content;
        const newContent = currentContent 
          ? `${currentContent}\n\n${update.content}`
          : update.content;
        updatedSections[update.index] = {
          ...updatedSections[update.index],
          content: newContent
        };
      });
      
      setJournalSections(updatedSections);
      
      // Clear the streaming text
      setStreamingText('');
      accumulatedTextRef.current = '';
      cleanedTextRef.current = '';
      
      // Mark as having unsaved changes
      setHasUnsavedChanges(true);
      
      const updatedTitles = sectionsToUpdate.map(u => updatedSections[u.index].title);
      toast.success(`Anteckning tillagd under: ${updatedTitles.join(', ')}`);
      
    } catch (error) {
      console.error('Error creating journal entry:', error);
      toast.error('Kunde inte skapa anteckning: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsProcessing(false);
    }
  };

  // ===========================================================
  // UI Controls
  // ===========================================================
  const handleStartDictation = () => {
    if (!selectedTemplate) {
      toast.error('Välj en journalmall först');
      return;
    }
    startRecording();
  };

  const handleToggleRecording = () => {
    if (!isRecording && !isPaused) {
      handleStartDictation();
    } else if (isRecording) {
      pauseRecording();
    } else if (isPaused) {
      resumeRecording();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ===========================================================
  // Render
  // ===========================================================
  if (loadingTemplates) {
    return (
      <div className="smart-dictation-container">
        <div className="loading-message">Laddar journalmallar...</div>
      </div>
    );
  }

  return (
    <div className="smart-dictation-container">
      {!templateLocked && (
        <TopBar 
          showTemplateDropdown={true}
          templates={templates}
          selectedTemplate={selectedTemplate}
          onTemplateChange={handleTemplateChange}
          showTabs={false}
          showSystemAudioControl={false}
          microphones={microphones}
          selectedMicId={selectedMicId}
          onChangeMic={setSelectedMicId}
        />
      )}
      
      <div className="smart-dictation-content">
        {!selectedTemplate ? (
          <div className="template-selection-prompt">
            <h2>Smart Diktering</h2>
            <p>Välj en journalmall för att börja diktera</p>
            <div className="template-info">
              <p>Med Smart Diktering kan du:</p>
              <ul>
                <li>Diktera fritt enligt mallens rubriker</li>
                <li>Se transkriptionen i realtid</li>
                <li>Få texten automatiskt placerad under rätt rubriker</li>
                <li>Fortsätt diktera och skapa anteckningar i flera omgångar</li>
                <li>Generera dokument och överför till journalsystem</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            {dictationStarted && (
              <div className="tabs-container">
                <div className="tabs">
                  <button 
                    className={`tab ${currentTab === 'journal' ? 'active' : ''}`}
                    onClick={() => setCurrentTab('journal')}
                  >
                    Journal
                  </button>
                  <button 
                    className={`tab ${currentTab === 'documents' ? 'active' : ''}`}
                    onClick={() => setCurrentTab('documents')}
                    disabled={journalSections.every(section => !section.content.trim())}
                  >
                    Dokument
                  </button>
                  {activeDocuments.map(doc => (
                    <button
                      key={doc.docId}
                      className={`tab ${currentTab === `document-${doc.docId}` ? 'active' : ''}`}
                      onClick={() => setCurrentTab(`document-${doc.docId}`)}
                    >
                      {doc.title}
                      <button
                        className="close-tab"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseDocument(doc.docId);
                        }}
                      >
                        ×
                      </button>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="tab-content">
              {currentTab === 'journal' && (
                <div>
                  {journalSections.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      {selectedTemplate ? 
                        'Laddar journalsektioner...' : 
                        'Välj en journalmall för att se sektioner'
                      }
                    </div>
                  ) : (
                    <JournalTab
                      sections={journalSections}
                      selectedTemplate={selectedTemplate}
                      displayDate={null}
                      hasPartial={false}
                      hasFinal={true}
                      isTranscribing={false}
                      onSectionChange={handleSectionChange}
                      onDeleteSection={handleDeleteSection}
                      onCopyContent={handleCopySection}
                      readOnly={false}
                      sessionId={sessionId}
                    />
                  )}
                </div>
              )}
              
              {currentTab === 'documents' && (
                <DocumentsTab
                  clinicId={user?.clinic_id}
                  openDocs={activeDocuments}
                  onGenerateDocument={handleGenerateDocument}
                />
              )}
              
              {activeDocuments.map(doc => 
                currentTab === `document-${doc.docId}` && (
                  <DocumentTab
                    key={doc.docId}
                    doc={doc}
                    onChangeSection={() => {}}
                    handleCopyContent={(text) => {
                      navigator.clipboard.writeText(text);
                      toast.success('Innehåll kopierat!');
                    }}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom bar - always visible when template is selected */}
      {selectedTemplate && (
        <div className="dictation-bottombar">
          <div className="streaming-text-area">
            <textarea
              className="streaming-textarea"
              value={streamingText}
              onChange={(e) => {
                setStreamingText(e.target.value);
                accumulatedTextRef.current = e.target.value;
                setHasUnsavedChanges(true);
              }}
              placeholder={dictationStarted ? "Dikterad text visas här..." : "Tryck på 'Börja diktera' för att starta"}
              readOnly={isRecording}
            />
          </div>
          
          <div className="dictation-controls">
            {!dictationStarted ? (
              <button
                className="start-dictation-btn"
                onClick={handleStartDictation}
                disabled={!selectedTemplate}
              >
                Börja diktera
              </button>
            ) : (
              <>
                <button
                  className={`control-btn ${isPaused ? 'resume' : isRecording ? 'pause' : 'start'}`}
                  onClick={handleToggleRecording}
                >
                  {isPaused ? 'Återuppta' : isRecording ? 'Pausa' : 'Börja'}
                </button>
                
                {(isRecording || isPaused) && (
                  <div className="dictation-timer">
                    {formatTime(currentTime)}
                  </div>
                )}
                
                <button
                  className="create-journal-btn"
                  onClick={handleCreateJournalEntry}
                  disabled={!streamingText.trim() || isProcessing}
                >
                  {isProcessing ? 'Skapar...' : 'Skapa anteckning'}
                </button>
              </>
            )}
          </div>
          
          {dictationStarted && (
            <div className="transfer-section">
              <button
                className={`transfer-btn ${canTransfer ? 'enabled' : 'disabled'}`}
                onClick={handleTransfer}
                disabled={!canTransfer || transferStatus === 'transferring'}
              >
                {transferStatus === 'transferring' ? 'Överför...' : 'Överför'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SmartDictation;